import {
  API_NETWORK_UNAVAILABLE,
  API_REQUEST_FAILED,
  API_TIMEOUT,
  ADAPTIVE_RT_PLANS_MISSING,
  CALC_PERMISSION_DENIED,
  calcQuotaExceeded,
} from '@/constants/userMessages';

const MAX_ERROR_MESSAGE_LENGTH = 500;

/**
 * Extract a short user-facing message from plain text or Django debug HTML.
 * @param {string} raw
 */
function extractMessageFromHtmlOrText(raw) {
  if (!raw.startsWith('<')) {
    return raw.length > MAX_ERROR_MESSAGE_LENGTH
      ? `${raw.slice(0, MAX_ERROR_MESSAGE_LENGTH)}…`
      : raw;
  }

  const exceptionMatch = raw.match(
    /<pre class="exception_value">([\s\S]*?)<\/pre>/i
  );
  if (exceptionMatch?.[1]) {
    return decodeHtmlEntities(exceptionMatch[1].trim());
  }

  const titleMatch = raw.match(/<title>([^<]+)<\/title>/i);
  if (titleMatch?.[1]) {
    const title = decodeHtmlEntities(titleMatch[1].trim());
    const atIdx = title.indexOf(' at ');
    return atIdx > 0 ? title.slice(0, atIdx) : title;
  }

  return API_REQUEST_FAILED;
}

const FIELD_LABELS = {
  username: 'Имя пользователя',
  email: 'Эл. почта',
  password: 'Пароль',
  old_password: 'Текущий пароль',
  new_password: 'Новый пароль',
  new_password2: 'Подтверждение пароля',
  last_name: 'Фамилия',
  first_name: 'Имя',
  patronymic: 'Отчество',
  position: 'Должность',
  workplace: 'Место работы',
  organization_name: 'Название организации',
  contact_person: 'Контактное лицо',
  user_type: 'Тип пользователя',
};

const SKIPPED_ERROR_KEYS = new Set(['code', 'used', 'limit', 'period']);

function messageList(value) {
  if (typeof value === 'string') {
    const text = value.trim();
    return text ? [text] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => messageList(item));
  }
  if (value && typeof value === 'object') {
    return Object.values(value).flatMap((item) => messageList(item));
  }
  return [];
}

/**
 * DRF field errors ({username: ["..."], non_field_errors: ["..."]}) become one sentence.
 * @param {Record<string, unknown>} data
 * @returns {string}
 */
function localizeKnownApiMessage(message) {
  if (/^Plans? not found:/i.test(message)) return ADAPTIVE_RT_PLANS_MISSING;
  return message;
}

function messageFromApiData(data) {
  if (data.code === 'quota_exceeded') {
    return calcQuotaExceeded(data.used, data.limit, data.period);
  }
  if (data.code === 'permission_denied') {
    return CALC_PERMISSION_DENIED;
  }
  if (typeof data.error === 'string' && data.error.trim()) {
    return localizeKnownApiMessage(data.error.trim());
  }

  const parts = [];
  Object.entries(data).forEach(([key, value]) => {
    if (SKIPPED_ERROR_KEYS.has(key)) return;
    const messages = messageList(value);
    if (!messages.length) return;
    const text = messages.join(' ');
    const label = FIELD_LABELS[key];
    parts.push(label ? `${label}: ${text}` : text);
  });
  return parts.join(' ');
}

function decodeHtmlEntities(text) {
  return text
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/**
 * Normalize axios errors for logging and optional user-facing messages.
 * @param {unknown} error
 * @returns {{ status?: number, message: string, isNetwork: boolean, raw?: unknown }}
 */
export function toApiError(error) {
  if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
    const status = error.response?.status;
    const data = error.response?.data;
    let message = API_REQUEST_FAILED;
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      message = API_NETWORK_UNAVAILABLE;
      return { status, message, isNetwork: true, raw: error };
    }
    if (error.code === 'ECONNABORTED') {
      message = API_TIMEOUT;
      return { status, message, isNetwork: true, raw: error };
    }
    if (typeof data === 'string' && data.trim()) {
      message = extractMessageFromHtmlOrText(data.trim());
    } else if (data && typeof data === 'object') {
      const parsed = messageFromApiData(data);
      if (parsed) message = parsed;
    }
    return { status, message, isNetwork: false, raw: error };
  }
  if (error instanceof Error) {
    return { message: error.message, isNetwork: false, raw: error };
  }
  return { message: String(error), isNetwork: false, raw: error };
}

/**
 * Сообщение для UI: ответ API / Error.message или запасной текст из userMessages.
 * @param {unknown} error
 * @param {string} fallback
 */
export function apiErrorMessage(error, fallback) {
  const parsed = toApiError(error);
  if (parsed.isNetwork) return parsed.message;
  if (!parsed.message || parsed.message === API_REQUEST_FAILED) return fallback;
  return parsed.message;
}
